import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { USE_API } from './config/env';
import { modamaiTrace } from './utils/modamaiTrace';
import { useAppDispatch } from './store/hooks';
import { fetchStoreSettings } from './store/actions/storeSettingsActions';
import { fetchCategories } from './store/actions/categoryActions';
import { fetchAuthMe } from './store/actions/authActions';
import MetaVerificationTags from './components/common/MetaVerificationTags';
import GlobalRouteSeo from './components/seo/GlobalRouteSeo';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { CartDrawer } from './components/commerce/CartDrawer';
import { ChatWidget } from './components/chat';
import Home from './pages/Home';
import Search from './pages/Search';
import Shop from './pages/Shop';
import Collections from './pages/Collections';
import CollectionDetail from './pages/CollectionDetail';
import Product from './pages/Product';
import Lookbook from './pages/Lookbook';
import About from './pages/About';
import CustomerService from './pages/CustomerService';
import Contact from './pages/Contact';
import Checkout from './pages/Checkout';
import NotFound from './pages/NotFound';
import SizeGuide from './pages/SizeGuide';
import ShippingInfo from './pages/ShippingInfo';
import ReturnsInfo from './pages/ReturnsInfo';
import ReviewsPage from './pages/ReviewsPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Account from './pages/Account';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import FAQ from './pages/FAQ';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import CookiePolicy from './pages/CookiePolicy';
import Sustainability from './pages/Sustainability';
import Accessibility from './pages/Accessibility';
import Careers from './pages/Careers';

function LegacyLocaleRedirect() {
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const localePrefixes = ['us', 'uk', 'fr', 'es', 'de', 'it', 'pt', 'nl', 'co', 'ar', 'mx', 'au', 'ca'];
  if (!parts.length || !localePrefixes.includes(parts[0].toLowerCase())) return <NotFound />;
  const to = `/${parts.slice(1).join('/')}${location.search || ''}`;
  return <Navigate to={to === '/' ? '/' : to} replace />;
}

function LegacyCategoryRedirect() {
  const { categorySlug = '' } = useParams();
  const safe = encodeURIComponent(String(categorySlug).trim());
  return <Navigate to={safe ? `/shop/${safe}` : '/shop'} replace />;
}

function LegacySubcategoryRedirect() {
  const { subcategorySlug = '', parentSlug = '' } = useParams();
  const parent = String(parentSlug || '').trim();
  const child = String(subcategorySlug || '').trim();
  const safeParent = encodeURIComponent(parent);
  const safeChild = encodeURIComponent(child);
  return <Navigate to={safeParent && safeChild ? `/shop/${safeParent}/${safeChild}` : '/shop'} replace />;
}

function StoreBootstrap() {
  const dispatch = useAppDispatch();
  useEffect(() => {
    modamaiTrace('StoreBootstrap mount', { USE_API });
    if (!USE_API) return;
    modamaiTrace('StoreBootstrap dispatch bootstrap actions', {});
    dispatch(fetchStoreSettings());
    dispatch(fetchCategories());
    dispatch(fetchAuthMe());
  }, [dispatch]);
  return null;
}

function App() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <StoreBootstrap />
      <MetaVerificationTags />
      <GlobalRouteSeo />
      <Header />
      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:category" element={<Shop />} />
          <Route path="/shop/:category/:subcategory" element={<Shop />} />
          <Route path="/collections" element={<Collections />} />
          <Route path="/collection/:slug" element={<CollectionDetail />} />
          <Route path="/product/:slug" element={<Product />} />
          <Route path="/lookbook" element={<Lookbook />} />
          <Route path="/about" element={<About />} />
          <Route path="/customer-service" element={<CustomerService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/faqs" element={<Navigate to="/faq" replace />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsConditions />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/size-guide" element={<SizeGuide />} />
          <Route path="/shipping" element={<ShippingInfo />} />
          <Route path="/returns" element={<ReturnsInfo />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/account" element={<Account />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/new" element={<Navigate to="/shop/new" replace />} />
          <Route path="/sale" element={<Navigate to="/shop/sale" replace />} />
          <Route path="/women" element={<Navigate to="/shop/women" replace />} />
          <Route path="/men" element={<Navigate to="/shop/men" replace />} />
          <Route path="/accessories" element={<Navigate to="/shop/accessories" replace />} />
          <Route path="/categories" element={<Navigate to="/shop" replace />} />
          <Route path="/category/:categorySlug" element={<LegacyCategoryRedirect />} />
          <Route path="/category/:parentSlug/:subcategorySlug" element={<LegacySubcategoryRedirect />} />
          <Route path="/wishlist" element={<Navigate to="/account" replace />} />
          <Route path="/search" element={<Search />} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          <Route path="/account/orders/:id" element={<Navigate to="/account" replace />} />
          <Route path="/:locale/*" element={<LegacyLocaleRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
      <CartDrawer />
      <ChatWidget />
    </div>
  );
}

export default App;
